import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('knots_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('knots_token');
      localStorage.removeItem('knots_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (data) => api.post('/auth/login', data);
export const getMe = () => api.get('/auth/me');
export const getClientAccess = () => api.get('/auth/client-access');
export const changePassword = (data) => api.post('/auth/change-password', data);

// Users
export const getUsers = () => api.get('/users');
export const createUser = (data) => api.post('/users', data);
export const resetUserPassword = (userId) => api.post(`/users/${userId}/reset-password`);
export const deleteUser = (userId) => api.delete(`/users/${userId}`);

// Categories
export const getCategories = () => api.get('/categories');
export const createCategory = (data) => api.post('/categories', data);
export const updateCategory = (id, data) => api.put(`/categories/${id}`, data);
export const deleteCategory = (id) => api.delete(`/categories/${id}`);

// Subcategories
export const getSubcategories = (categoryId) => api.get('/subcategories', { params: { category_id: categoryId } });
export const createSubcategory = (data) => api.post('/subcategories', data);
export const updateSubcategory = (id, data) => api.put(`/subcategories/${id}`, data);
export const deleteSubcategory = (id) => api.delete(`/subcategories/${id}`);

// Projects
export const getProjects = () => api.get('/projects');
export const getProject = (id) => api.get(`/projects/${id}`);
export const createProject = (data) => api.post('/projects', data);
export const updateProject = (id, data) => api.put(`/projects/${id}`, data);
export const deleteProject = (id) => api.delete(`/projects/${id}`);
export const exportProjectPDF = (id) => api.get(`/projects/${id}/export-pdf`, { responseType: 'blob' });

// Budget Items
export const getBudgetItems = (projectId) => api.get('/budget-items', { params: { project_id: projectId } });
export const createBudgetItem = (data) => api.post('/budget-items', data);
export const updateBudgetItem = (id, data) => api.put(`/budget-items/${id}`, data);
export const deleteBudgetItem = (id) => api.delete(`/budget-items/${id}`);

// Installments
export const getInstallments = (budgetItemId) => api.get('/installments', { params: { budget_item_id: budgetItemId } });
export const createInstallment = (data) => api.post('/installments', data);
export const updateInstallment = (id, data) => api.put(`/installments/${id}`, data);
export const markInstallmentPaid = (id) => api.post(`/installments/${id}/pay`);
export const uploadPaymentProof = (id, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/installments/${id}/upload-proof`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const deleteInstallment = (id) => api.delete(`/installments/${id}`);
export const getPaymentProofUrl = (filename) => `${API_URL}/api/payment-proof/${filename}`;

// Logs
export const getLoginLogs = () => api.get('/logs/login');
export const getSystemLogs = () => api.get('/logs/system');

// Invitation Projects
export const getInvitationProjects = () => api.get('/invitations');
export const getInvitationProject = (id) => api.get(`/invitations/${id}`);
export const createInvitationProject = (data) => api.post('/invitations', data);
export const updateInvitationProject = (id, data) => api.put(`/invitations/${id}`, data);
export const deleteInvitationProject = (id) => api.delete(`/invitations/${id}`);

// Sangjit RSVPs
export const getSangjitRsvps = (invitationId) => api.get(`/invitations/${invitationId}/rsvps`);
export const getAllRsvps = () => api.get('/rsvps/all');
export const createSangjitRsvp = (invitationId, data) => api.post(`/invitations/${invitationId}/rsvps`, data);
export const updateSangjitRsvp = (rsvpId, data) => api.put(`/rsvps/${rsvpId}`, data);
export const deleteSangjitRsvp = (rsvpId) => api.delete(`/rsvps/${rsvpId}`);

export default api;
